<script setup lang="ts">
import { CmsElementProductListing } from '@shopware-pwa/composables-next';
import { computed, ref } from 'vue';
import SwProductCard from '../SwProductCard.vue';
import { ShopwareSearchParams } from '@shopware-pwa/types';
import deepMerge from '../../../helpers/deepMerge';
import getTranslations from '../../../helpers/getTranslations';
import type { IOnlineViewer } from '@upa/design-system';
import { Loader } from '@upa/design-system';
import { Modal } from '@upa/design-system';
import { Pagination } from '@upa/design-system';

const props = defineProps<{
  content: CmsElementProductListing;
}>();

type Translations = {
  listing: {
    noProducts: string;
  };
};
let translations: Translations = {
  listing: { noProducts: 'No products found 😔' },
};
const globalTranslations = getTranslations();
translations = deepMerge(translations, globalTranslations) as Translations;
const route = useRoute();

// Listing loading
const { isLoading, setLoading } = useListingLoading();

// Listing filters
const { initializeFilters, isFiltersInitialized } = useListingFilters();

// Listing sortings
const { initializeSorting, isSortingInitialized } = useListingSorting();

// Listing setup for category
const {
  search: fetchListing,
  getElements,
  setInitialListing,
  getInitialFilters,
  getCurrentFilters,
  getSortingOrders,
  getCurrentSortingOrder,
  getCurrentPage,
  changeCurrentPage,
  getTotalPagesCount,
  getTotal,
  // getCurrentListing
} = useListing({ listingType: 'categoryListing' });

// Listing setup for search results
const { search: fetchSearch, getCurrentListing } = useListing({ listingType: 'productSearchListing' });

// if (route.query.query) {
//   ({
//     search,
//     getElements,
//     setInitialListing,
//     getCurrentPage,
//     changeCurrentPage,
//     getTotalPagesCount,
//     getTotal,
//     getCurrentListing
//   } = useListing({ listingType: 'productSearchListing' }));

//   await search({
//     limit: 20,
//     p: route.query.p || 1,
//     query: route.query.query,
//     properties: route.query.properties
//   } as Partial<ShopwareSearchParams>), { preventRouteChange: true };

//   if (getCurrentListing.value){
//     setInitialListing(getCurrentListing.value);
//   }
// } else {
  setLoading(true);
  await setInitialListing(props?.content?.data?.listing);

  // Initialize filters
  if (!isFiltersInitialized()) {
    initializeFilters(getInitialFilters, getCurrentFilters);
  }

  // Initialize sortings
  if (!isSortingInitialized()) {
    initializeSorting(getSortingOrders, getCurrentSortingOrder);
  }

  // If a searchable value is present, the search request will be handled
  // in SwProductListingFilters.vue, and the loader will be hidden there.
  // If no searchable value is present, hide the loader here instead.
  if (!route.query.query) {
    setLoading(false);
  }
// }

const stItem = (route.query?.stItem || route.query?.stitem) as string;
if(stItem?.length) {
  const items = stItem.split(',');
  fetchListing({
    filter: [
      {
        type: 'equalsAny',
        field: 'productNumber',
        value: items
      }
    ]
  });
}

const router = useRouter();

const changePage = async (page: number) => {
  await router.push({
    query: {
      ...route.query,
      p: page,
    },
  });
  if (route.query.query) {
    setLoading(true);
    await fetchSearch({
      ...route.query,
      p: page,
      limit: 20,
    } as Partial<ShopwareSearchParams>), { preventRouteChange: true };
    if (getCurrentListing.value){
      setInitialListing(getCurrentListing.value);
    }
    setLoading(false);
  } else {
    setLoading(true);
    await changeCurrentPage(page, {
      ...route.query,
      limit: 20,
    } as Partial<ShopwareSearchParams>);
    setLoading(false);
  }
};

useListingNavigation(async () => {
  if (route.query.query) {
    setLoading(true);
    await fetchSearch({
      ...route.query,
      limit: 20
    });
    if (getCurrentListing.value) {
      setInitialListing(getCurrentListing.value);
    }
    setLoading(false);
  } else {
    setLoading(true);
    await fetchListing({
      ...route.query,
      limit: 20
    });
    setLoading(false);
  }
});

const isProductListing = computed(
  () => props.content?.type === 'product-listing',
);

const viewer = ref<IOnlineViewer | null>(null);
const visible = ref(false);

const closeViewer = () => {
  visible.value = false;
  viewer.value = null;
};

/**
 * Only embeddable documents go into the modal. Everything else is refused by
 * the provider via X-Frame-Options, which cannot be caught once the iframe
 * exists, so it leaves the page instead of rendering a broken frame.
 */
function openViewer(target: IOnlineViewer) {
  if (!target) return;

  if (target.mode === 'external') {
    window.open(target.url, '_blank', 'noopener');
    return;
  }

  viewer.value = target;
  visible.value = true;
}
</script>

<template>
  <div class="bg-white cms-listing__container">
    <div class="listing">

      <div v-if="isLoading" style="height: 400px">
        <div
          class="relative flex items-center justify-around pt-6 pb-6"
          style="height: 100%"
        >
          <Loader :loading="isLoading"/>
        </div>
      </div>

      <div v-if="!isLoading && getElements.length" class="listing-content">
        <!-- // TODO: snippets, style, check if after filter change -->
        <div class="listing-total-amount">
          <span class="listing-total-amount__number">{{ getTotal }}</span>&nbsp;<span>{{ $t('listing.totalItems') }}</span>
        </div>
        <div class="flex flex-wrap">
          <SwProductCard
            v-for="product in getElements"
            :key="product.id"
            :product="product"
            :is-product-listing="isProductListing"
            class="listing-product-card"
            @open-viewer="openViewer"
          />
          <Modal
            v-if="visible && viewer"
            :top-centered="true"
            class="cms-el-product-listing-viewer-modal"
            @layer-click="closeViewer"
            @escape-press="closeViewer"
            @close="closeViewer"
          >
            <template #content>
              <iframe
                class="onlineBrowse__iframe"
                :src="viewer.url"
                :title="$t('listing.browseOnline')"
                allowfullscreen
                allow="fullscreen"
                referrerpolicy="strict-origin-when-cross-origin"
              />
            </template>
          </Modal>
        </div>
        <div class="pagination__wrapper">
          <Pagination
            :total="getTotalPagesCount"
            :current="Number(getCurrentPage)"
            @change-page="changePage"
          />
        </div>
      </div>
      <div v-else-if="!isLoading">
        <h2 class="mx-auto text-center">
          {{ translations.listing.noProducts }}
        </h2>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.cms-listing__container {
  margin: -20px;
}

.listing {
  padding: 20px 0;
}

.listing-product-card {
  @media (min-width: 1498px) {
    // flex-grow: inherit;
    flex: 0 1 33%;
  }
}
</style>
